import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { DESTINATIONS_FEATURED, NIGERIA_STATES, stateHref, stateSlug } from "@/data/content";
import { FadeUp, SectionHeading } from "./Reveal";

export const Destinations = () => (
  <section
    id="destinations"
    data-testid="destinations-section"
    className="border-y border-jawa-stone bg-jawa-stone/40"
  >
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <FadeUp className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading overline="Explore Africa" title="Africa, beautifully within reach." />
        <p className="max-w-sm text-base leading-relaxed text-jawa-espresso/70">
          Ambitious cities, beautiful coastlines and every Nigerian state in between — each card opens
          its own destination page.
        </p>
      </FadeUp>

      <div className="mt-16 grid auto-rows-[240px] gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {DESTINATIONS_FEATURED.map((d, i) => (
          <FadeUp
            key={d.slug}
            delay={i * 0.07}
            className={d.span === "wide" ? "lg:col-span-2" : d.span === "tall" ? "lg:row-span-2" : ""}
          >
            <Link
              to={`/destinations/${d.slug}`}
              data-testid={`destination-card-${d.slug}`}
              className="group relative block h-full cursor-pointer overflow-hidden"
            >
              <img
                src={d.image}
                alt={d.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/75 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                <div>
                  <h3 className="font-serif text-2xl font-medium tracking-tight text-jawa-ivory">{d.name}</h3>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-jawa-ivory/70">{d.tag}</p>
                </div>
                <MapPin className="h-5 w-5 text-jawa-gold transition-all duration-300 group-hover:-translate-y-1 group-hover:text-jawa-turquoise" />
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>

      <FadeUp className="mt-20">
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <div className="flex items-baseline gap-4">
            <h3 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">
              Nigeria, state by state
            </h3>
            <span className="text-xs uppercase tracking-[0.25em] text-jawa-terracotta">36 States + FCT</span>
          </div>
          <Link
            to="/destinations"
            data-testid="destinations-view-all"
            className="text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
          >
            View all destinations
          </Link>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="nigeria-states-list">
          {NIGERIA_STATES.map((s) => (
            <Link
              key={s}
              to={stateHref(s)}
              data-testid={`state-link-${stateSlug(s)}`}
              className={`cursor-pointer border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                s.includes("FCT") || s === "Lagos"
                  ? "border-jawa-gold bg-jawa-gold/10 text-jawa-espresso hover:bg-jawa-gold/25"
                  : "border-jawa-espresso/20 text-jawa-espresso/70 hover:border-jawa-terracotta hover:text-jawa-terracotta"
              }`}
            >
              {s}
            </Link>
          ))}
        </div>
      </FadeUp>
    </div>
  </section>
);
